import { useState, useMemo, useRef } from 'react'
import { useFinance } from '../../context/FinanceContext'
import { getThemeColors } from '../../lib/constants'
import { formatCurrency } from '../../lib/formatters'
import { calculateBabyCost } from '../../lib/calculations/babyCost'
import { addChartToPDF } from '../../lib/pdfChartCapture'
import GlassCard from '../ui/GlassCard'
import Button from '../ui/Button'
import { useSubscription } from '../../context/SubscriptionContext'
import { trackExportReport } from '../../lib/analytics'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts'

function ResultCard({ label, value, color, highlight }) {
  return (
    <GlassCard className={`!p-4 ${highlight ? 'ring-1' : ''}`} style={highlight ? { borderColor: color + '44' } : {}}>
      <p className="text-xs text-muted mb-1">{label}</p>
      <p className={`font-bold ${highlight ? 'text-xl' : 'text-lg'}`} style={{ color }}>{value}</p>
    </GlassCard>
  )
}

function SelectField({ label, value, onChange, options, colors }) {
  return (
    <div>
      <label className="block text-xs font-medium text-muted mb-1.5">{label}</label>
      <div className="flex flex-wrap gap-1">
        {options.map(opt => (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            className={`px-3 py-2 rounded-xl text-xs font-medium transition-all ${
              value === opt.value ? 'text-heading shadow-sm' : 'text-muted hover:text-heading'
            }`}
            style={value === opt.value ? {
              background: `linear-gradient(135deg, ${colors.primary}22, ${colors.secondary}22)`,
              border: `1px solid ${colors.primary}44`,
            } : { border: '1px solid transparent' }}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  )
}

export default function BabyCostEstimator() {
  const { state } = useFinance()
  const { isPro } = useSubscription()
  const colors = getThemeColors(state.theme)
  const chartRef = useRef(null)

  const [locationTier, setLocationTier] = useState('mcol')
  const [insuranceType, setInsuranceType] = useState('employer')
  const [childcareType, setChildcareType] = useState('daycare')
  const [existingKids, setExistingKids] = useState(0)
  const [householdIncome, setHouseholdIncome] = useState(100000)
  const [filingStatus, setFilingStatus] = useState('mfj')

  const result = useMemo(() => calculateBabyCost({
    locationTier, insuranceType, childcareType, existingKids, householdIncome, filingStatus,
  }), [locationTier, insuranceType, childcareType, existingKids, householdIncome, filingStatus])

  const projectionData = result.fiveYearProjection.map(y => ({
    year: `Year ${y.year}`,
    'Gross Cost': y.annualCost,
    'Tax Credit': y.taxCredit,
    'Net Cost': y.netCost,
  }))

  const locationLabels = { lcol: 'Low Cost (LCOL)', mcol: 'Medium Cost (MCOL)', hcol: 'High Cost (HCOL)' }
  const insuranceLabels = { employer: 'Employer', marketplace: 'Marketplace', medicaid: 'Medicaid' }
  const childcareLabels = { none: 'Stay-at-Home', family: 'Family/Part-time', daycare: 'Daycare', nanny: 'Nanny' }
  const filingLabels = { mfj: 'Married Filing Jointly', single: 'Single', hoh: 'Head of Household' }

  const handleExportPDF = async () => {
    trackExportReport()
    const { default: jsPDF } = await import('jspdf')
    const { default: autoTable } = await import('jspdf-autotable')

    const doc = new jsPDF()
    const pageWidth = doc.internal.pageSize.getWidth()
    const margin = 14

    // --- Page 1: Title, Inputs, Key Results, Monthly Breakdown ---

    // Title
    doc.setFontSize(22)
    doc.setTextColor(0)
    doc.text('Baby Cost Estimate Report', margin, 22)
    doc.setFontSize(10)
    doc.setTextColor(128)
    doc.text(`Generated by Liquid Finance  |  ${new Date().toLocaleDateString()}`, margin, 30)

    // Section: Your Situation (Inputs)
    doc.setFontSize(14)
    doc.setTextColor(0)
    doc.text('Your Situation', margin, 42)

    const inputRows = [
      ['Household Income', formatCurrency(householdIncome)],
      ['Filing Status', filingLabels[filingStatus] || filingStatus],
      ['Location Tier', locationLabels[locationTier] || locationTier],
      ['Insurance Type', insuranceLabels[insuranceType] || insuranceType],
      ['Childcare Plan', childcareLabels[childcareType] || childcareType],
      ['Existing Children', existingKids === 0 ? 'None (First Baby)' : `${existingKids}`],
      ['Total Children (with new baby)', `${result.totalChildren}`],
    ]

    autoTable(doc, {
      head: [['Input', 'Value']],
      body: inputRows,
      startY: 46,
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
    })

    // Section: Key Results (stat cards)
    let y = doc.lastAutoTable.finalY + 10
    doc.setFontSize(14)
    doc.setTextColor(0)
    doc.text('Key Results', margin, y)
    y += 4

    const keyResultRows = [
      ['Monthly Cost', formatCurrency(result.totalMonthly)],
      ['First Year Total (with setup)', formatCurrency(result.firstYearWithSetup)],
      ['One-Time Setup Costs', formatCurrency(result.oneTimeCosts)],
      [`Family Tax Credit (${result.totalChildren} ${result.totalChildren === 1 ? 'child' : 'children'})`, formatCurrency(result.totalFamilyCredit)],
      ['Net First Year Cost', formatCurrency(result.netFirstYear)],
    ]

    autoTable(doc, {
      head: [['Metric', 'Value']],
      body: keyResultRows,
      startY: y,
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
    })

    // Section: Monthly Breakdown
    y = doc.lastAutoTable.finalY + 10
    doc.setFontSize(14)
    doc.setTextColor(0)
    doc.text('Monthly Breakdown', margin, y)
    y += 4

    const breakdownRows = result.monthlyBreakdown
      .filter(b => b.amount > 0)
      .map(item => [
        item.category,
        formatCurrency(item.amount),
        `${Math.round((item.amount / result.totalMonthly) * 100)}%`,
      ])
    breakdownRows.push(['Total Monthly', formatCurrency(result.totalMonthly), '100%'])

    autoTable(doc, {
      head: [['Category', 'Monthly Cost', '% of Total']],
      body: breakdownRows,
      startY: y,
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
      didParseCell: (data) => {
        // Bold the total row
        if (data.row.index === breakdownRows.length - 1) {
          data.cell.styles.fontStyle = 'bold'
        }
      },
    })

    // Section: Tax Credit Details (mirrors the conditional UI under the breakdown)
    y = doc.lastAutoTable.finalY + 6
    doc.setFontSize(11)
    doc.setTextColor(0)

    const creditLines = []
    if (result.totalFamilyCredit > 0) {
      if (result.existingCredit > 0) {
        creditLines.push(`Existing Credit (${existingKids} ${existingKids === 1 ? 'child' : 'children'}): ${formatCurrency(result.existingCredit)}/yr`)
      }
      creditLines.push(`New Baby Credit: ${result.childTaxCredit > 0 ? `+${formatCurrency(result.childTaxCredit)}/yr` : '$0 (phased out)'}`)
      creditLines.push(`Total Family Credit: ${formatCurrency(result.totalFamilyCredit)}/yr (${formatCurrency(Math.round(result.totalFamilyCredit / 12))}/mo)`)
      if (result.isPhaseOut) {
        creditLines.push(`Note: Credit reduced due to income above ${filingStatus === 'mfj' ? '$400,000' : '$200,000'} threshold`)
      }
    } else {
      creditLines.push('Child Tax Credit: $0 (income phase-out)')
    }

    doc.setFontSize(10)
    doc.setTextColor(60)
    creditLines.forEach(line => {
      // Check page break
      if (y > doc.internal.pageSize.getHeight() - 20) {
        doc.addPage()
        y = margin
      }
      doc.text(line, margin, y)
      y += 5
    })

    // --- Page 2: 5-Year Projection Table + Chart ---
    doc.addPage()

    doc.setFontSize(16)
    doc.setTextColor(0)
    doc.text('5-Year Cost Projection', margin, 22)

    const projectionRows = result.fiveYearProjection.map(yr => [
      `Year ${yr.year}`,
      formatCurrency(yr.annualCost),
      formatCurrency(yr.taxCredit),
      formatCurrency(yr.netCost),
    ])

    autoTable(doc, {
      head: [['Year', 'Gross Cost', 'Tax Credit', 'Net Cost']],
      body: projectionRows,
      startY: 28,
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
    })

    // Explanatory text (matches UI footer under chart)
    y = doc.lastAutoTable.finalY + 6
    doc.setFontSize(9)
    doc.setTextColor(100)
    const projectionNote = `Assumes 3% annual inflation. Diapers cost drops after year 3. One-time setup costs (${formatCurrency(result.oneTimeCosts)}) included in year 1.`
    const splitNote = doc.splitTextToSize(projectionNote, pageWidth - margin * 2)
    doc.text(splitNote, margin, y)

    // Chart capture (adds its own page)
    await addChartToPDF(doc, chartRef, { title: '5-Year Cost Projection Chart' })

    doc.save(`baby-cost-estimate-${Date.now()}.pdf`)
  }

  return (
    <div className="space-y-6">
      {/* Inputs */}
      <GlassCard>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h3 className="text-lg font-semibold text-heading">Your Situation</h3>
          <Button variant="secondary" size="sm" onClick={handleExportPDF} disabled={!isPro} title={!isPro ? 'Pro feature' : ''}>
            Export PDF{!isPro ? ' (Pro)' : ''}
          </Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <SelectField label="Location Tier" value={locationTier} onChange={setLocationTier} colors={colors} options={[
            { value: 'lcol', label: 'Low Cost (LCOL)' },
            { value: 'mcol', label: 'Medium Cost (MCOL)' },
            { value: 'hcol', label: 'High Cost (HCOL)' },
          ]} />
          <SelectField label="Insurance Type" value={insuranceType} onChange={setInsuranceType} colors={colors} options={[
            { value: 'employer', label: 'Employer' },
            { value: 'marketplace', label: 'Marketplace' },
            { value: 'medicaid', label: 'Medicaid' },
          ]} />
          <SelectField label="Childcare Plan" value={childcareType} onChange={setChildcareType} colors={colors} options={[
            { value: 'none', label: 'Stay-at-Home' },
            { value: 'family', label: 'Family/Part-time' },
            { value: 'daycare', label: 'Daycare' },
            { value: 'nanny', label: 'Nanny' },
          ]} />
          <SelectField label="Existing Children" value={existingKids} onChange={setExistingKids} colors={colors} options={[
            { value: 0, label: 'None (First Baby)' },
            { value: 1, label: '1 Child' },
            { value: 2, label: '2 Children' },
            { value: 3, label: '3+ Children' },
          ]} />
          <div>
            <label className="block text-xs font-medium text-muted mb-1.5">Household Income</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-faint text-sm">$</span>
              <input
                type="number"
                value={householdIncome}
                onChange={e => setHouseholdIncome(parseFloat(e.target.value) || 0)}
                className="w-full bg-input border border-input rounded-xl py-2.5 pl-7 pr-3 text-sm text-heading focus:outline-none focus:border-primary transition"
              />
            </div>
          </div>
        </div>
      </GlassCard>

      {/* Key Results */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <ResultCard label="Monthly Cost" value={formatCurrency(result.totalMonthly)} color={colors.primary} highlight />
        <ResultCard label="First Year Total" value={formatCurrency(result.firstYearWithSetup)} color={colors.negative} highlight />
        <ResultCard label={`Family Tax Credit (${result.totalChildren} ${result.totalChildren === 1 ? 'child' : 'children'})`} value={formatCurrency(result.totalFamilyCredit)} color={colors.positive} />
        <ResultCard label="Net First Year" value={formatCurrency(result.netFirstYear)} color={colors.warning} highlight />
      </div>

      {/* Monthly Breakdown */}
      <GlassCard>
        <h3 className="text-lg font-semibold text-heading mb-4">Monthly Breakdown</h3>
        <div className="space-y-2">
          {result.monthlyBreakdown.filter(b => b.amount > 0).map(item => (
            <div key={item.category} className="flex items-center justify-between">
              <span className="text-sm text-muted">{item.category}</span>
              <div className="flex items-center gap-3">
                <div className="w-20 h-2 rounded-full bg-subtle overflow-hidden">
                  <div className="h-full rounded-full" style={{
                    width: `${Math.min(100, (item.amount / result.totalMonthly) * 100)}%`,
                    backgroundColor: colors.primary,
                  }} />
                </div>
                <span className="text-sm font-medium text-heading w-16 text-right">{formatCurrency(item.amount)}</span>
              </div>
            </div>
          ))}
          <div className="border-t border-card pt-2 flex justify-between text-sm font-semibold">
            <span className="text-heading">Total Monthly</span>
            <span style={{ color: colors.primary }}>{formatCurrency(result.totalMonthly)}</span>
          </div>
          {result.totalFamilyCredit > 0 && (
            <>
              {result.existingCredit > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted">Existing Credit ({existingKids} {existingKids === 1 ? 'child' : 'children'})</span>
                  <span className="text-heading">{formatCurrency(result.existingCredit)}/yr</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-muted">New Baby Credit</span>
                <span style={{ color: colors.positive }}>{result.childTaxCredit > 0 ? `+${formatCurrency(result.childTaxCredit)}/yr` : '$0 (phased out)'}</span>
              </div>
              <div className="flex justify-between text-sm font-medium">
                <span className="text-muted">Total Family Credit</span>
                <span style={{ color: colors.positive }}>{formatCurrency(result.totalFamilyCredit)}/yr ({formatCurrency(Math.round(result.totalFamilyCredit / 12))}/mo)</span>
              </div>
              {result.isPhaseOut && (
                <p className="text-xs text-faint mt-1">Credit reduced due to income above {filingStatus === 'mfj' ? '$400,000' : '$200,000'} threshold</p>
              )}
            </>
          )}
          {result.totalFamilyCredit === 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-muted">Child Tax Credit</span>
              <span className="text-faint">$0 (income phase-out)</span>
            </div>
          )}
        </div>
      </GlassCard>

      {/* 5-Year Projection */}
      <GlassCard>
        <h3 className="text-lg font-semibold text-heading mb-4">5-Year Cost Projection</h3>
        <div className="h-64" ref={chartRef}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={projectionData}>
              <CartesianGrid strokeDasharray="3 3" stroke={colors.chartGrid} />
              <XAxis dataKey="year" tick={{ fontSize: 11, fill: colors.chartTick }} />
              <YAxis tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11, fill: colors.chartTick }} />
              <Tooltip formatter={v => formatCurrency(v)} contentStyle={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 12 }} />
              <Legend />
              <Bar dataKey="Net Cost" fill={colors.primary} radius={[4, 4, 0, 0]} />
              <Bar dataKey="Tax Credit" fill={colors.positive} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4 text-xs text-muted">
          <p>Assumes 3% annual inflation. Diapers cost drops after year 3. One-time setup costs ({formatCurrency(result.oneTimeCosts)}) included in year 1.</p>
        </div>
      </GlassCard>
    </div>
  )
}
